import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const MAX_POLLS = 15;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refreshSubscription } = useAuth();
  const [state, setState] = useState("polling"); // polling | success | error | expired
  const polls = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let timer;

    const poll = async () => {
      if (polls.current >= MAX_POLLS) { setState("expired"); return; }
      polls.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid" || data.status === "completed") {
          await refreshSubscription();
          setState("success");
          setTimeout(() => navigate("/dashboard"), 1800);
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") { setState("error"); return; }
      } catch (e) { console.debug("payment status poll error:", e?.message); }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => timer && clearTimeout(timer);
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-md text-center border border-border rounded-lg bg-card p-10" data-testid="payment-success-page">
        {state === "polling" && (
          <>
            <Loader2 className="h-10 w-10 mx-auto text-primary animate-spin" />
            <h1 className="font-heading text-2xl font-bold tracking-tight mt-5">Confirming your subscription…</h1>
            <p className="text-muted-foreground mt-2 text-sm">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="h-12 w-12 mx-auto text-[hsl(142_71%_50%)]" data-testid="payment-success-icon" />
            <h1 className="font-heading text-2xl font-bold tracking-tight mt-5">You're in! Trial started.</h1>
            <p className="text-muted-foreground mt-2 text-sm">Redirecting to your dashboard…</p>
          </>
        )}
        {(state === "error" || state === "expired") && (
          <>
            <XCircle className="h-12 w-12 mx-auto text-destructive" />
            <h1 className="font-heading text-2xl font-bold tracking-tight mt-5">{state === "expired" ? "Still processing" : "Something went wrong"}</h1>
            <p className="text-muted-foreground mt-2 text-sm">
              {state === "expired" ? "Your payment may still be confirming. Check your dashboard shortly." : "We couldn't confirm the payment."}
            </p>
            <Button className="mt-6" onClick={() => navigate("/pricing")} data-testid="back-to-pricing-button">Back to plans</Button>
          </>
        )}
      </div>
    </div>
  );
}
